<!--
  BetBuilderBadge — shared "BB"/"SGP" text-pill badge for all bet-builder
  render sites (event cards, cart legs, confirmed-bet timelines, share
  receipt, terminal variants, ...).

  This mirrors FeatureBadge.vue's admin-override precedence, but drives
  ONLY the badge's textual/image CONTENT — never its box size. Every call
  site keeps its own hardcoded CSS class (bb-badge / bb-badge.big /
  conf-bb-badge / sr-bb-badge / bb-badge-cart / bb-badge-leg), each with
  its own padding/font-size, applied via normal class attribute
  inheritance (no inheritAttrs:false needed — Vue merges a component's
  `class` onto its single root element automatically). The `size` field
  of settings/getBadgeIconConfig is intentionally IGNORED here: unlike
  earlyPayout/cashout these are single-line text pills, not fixed-box
  glyphs.

  Fed by the same per-platform admin override as earlyPayout/cashout —
  see FeatureBadge.vue for the fetch (src/main.js, GET /v3/badge-icons/:platformId)
  and Vuex wiring (settings/SET_BADGE_ICONS_CONFIG) — under the 'betBuilder'
  badgeKey, same response shape.

  Precedence (highest first):
    1. imageEnabled === true AND imageUrl is a non-empty string -> render
       the admin-uploaded image as a plain <img>, sized via CSS to fill
       whatever box the caller's own class defines (width:100%;height:100%;
       object-fit:contain), so it naturally inherits each site's own
       dimensions. Rendered via <img>, NEVER inlined into the DOM — same
       XSS-safety reasoning as FeatureBadge.vue: an <img> never executes
       script content embedded in an SVG file.
    2. Else "no override for the current locale" wins the same way as
       translation overrides do: if `enabled` is false, or there's no
       non-empty entry for `$i18n.locale`, the default text (the
       `defaultText` prop — today's hardcoded "BB"/"SGP") renders. Only a
       non-empty per-locale override swaps in the custom text.
    3. Else the `defaultText` prop renders.

  `tag` lets each call site keep its exact original root element (div vs
  span) so no incidental CSS/box behavior changes.
-->
<template>
  <img
    v-if="imageActive"
    :src="config.imageUrl"
    style="width:100%;height:100%;object-fit:contain"
    alt=""
  />
  <component :is="tag" v-else>{{ displayText }}</component>
</template>

<script>
export default {
    name: 'BetBuilderBadge',
    props: {
        defaultText: {
            type: String,
            default: 'BB'
        },
        tag: {
            type: String,
            default: 'span'
        }
    },
    computed: {
        config() {
            return this.$store.getters['settings/getBadgeIconConfig']('betBuilder');
        },
        // Precedence step 1: an admin-uploaded image wins over custom text
        // and over the default text whenever it's enabled and present.
        imageActive() {
            return this.config.imageEnabled === true &&
                typeof this.config.imageUrl === 'string' &&
                this.config.imageUrl.trim().length > 0;
        },
        customText() {
            if (!this.config.enabled) return null;
            const value = this.config.text?.[this.$i18n.locale];
            return (typeof value === 'string' && value.trim().length > 0) ? value : null;
        },
        displayText() {
            return this.customText || this.defaultText;
        }
    }
};
</script>
